// ── Keyword lists ─────────────────────────────────────────────────────────────
const COMMITMENT_WORDS = [
  'send', 'share', 'submit', 'deliver', 'call back', 'get back to',
  'follow up', 'follow-up', 'send over', 'pass on', 'forward',
];

const PROPOSAL_WORDS = ['proposal', 'brief', 'quote', 'contract'];

const MS_HOUR = 3600000;
const MS_DAY  = 86400000;

// ── Helpers ───────────────────────────────────────────────────────────────────
function daysAgoLabel(ms) {
  const days = Math.floor(ms / MS_DAY);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

function daysUntilLabel(ms) {
  const days = Math.floor(-ms / MS_DAY);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

function titleContains(title, words) {
  const lower = (title || '').toLowerCase();
  return words.some(w => lower.includes(w));
}


function contactForName(name, contacts) {
  if (!name) return null;
  const lower = name.toLowerCase();
  return contacts.find(c =>
    c.name?.toLowerCase() === lower ||
    c.company?.toLowerCase() === lower ||
    c.email?.toLowerCase() === lower
  ) ?? null;
}

// ── Trigger 1 — Post-meeting follow-up ───────────────────────────────────────
export function checkPostMeeting(tasks, googleEvents = [], icalEvents = [], outlookEvents = [], contacts = []) {
  const now  = Date.now();
  const prompts = [];

  // Collect meetings that ended 2 h – 48 h ago
  const recentWindow = 48 * MS_HOUR;
  const minGap       =  2 * MS_HOUR;

  // Helper to get end time of a meeting
  function endMs(m) {
    const raw = m.end || m.deadline || m.datetime;
    if (!raw) return NaN;
    return new Date(raw).getTime();
  }

  // Normalise all calendar sources into one list
  const allMeetings = [
    ...tasks.filter(t => !t.completed && t.taskType === 'meeting').map(t => ({
      id:       `task-${t.id}`,
      title:    t.title,
      end:      t.deadline || t.datetime,
      attendees: t.assignedTeam || [],
      source:   'task',
    })),
    ...googleEvents.map(e => ({
      id:       `google-${e.id || e.summary}`,
      title:    e.summary || e.title || 'Meeting',
      end:      e.end,
      attendees: (e.attendees || []).map(a => a.email || a).filter(Boolean),
      source:   'google',
    })),
    ...icalEvents.map(e => ({
      id:       `ical-${e.uid || e.title}`,
      title:    e.summary || e.title || 'Meeting',
      end:      e.end || e.datetime,
      attendees: [],
      source:   'ical',
    })),
    ...outlookEvents.map(e => ({
      id:       `outlook-${e.id || e.subject}`,
      title:    e.subject || e.title || 'Meeting',
      end:      e.end,
      attendees: (e.attendees || []).map(a => a.email || a).filter(Boolean),
      source:   'outlook',
    })),
  ];

  for (const meeting of allMeetings) {
    const endTime = endMs(meeting);
    if (isNaN(endTime)) continue;

    const elapsed = now - endTime;
    if (elapsed < minGap || elapsed > recentWindow) continue;

    // Check whether a follow-up task exists created after this meeting
    const hasFollowUp = tasks.some(t => {
      if (t.taskType === 'meeting') return false;
      const created = new Date(t.createdAt || t.datetime).getTime();
      return created > endTime && titleContains(t.title, ['follow', meeting.title.slice(0, 10).toLowerCase()]);
    });
    if (hasFollowUp) continue;

    // Find a contact name from attendees or title
    const attendeeContact = meeting.attendees.length
      ? contacts.find(c => meeting.attendees.some(a => a === c.email || a === c.id?.toString()))
      : null;

    const hoursAgo = Math.round(elapsed / MS_HOUR);
    const timeLabel = hoursAgo < 24 ? `${hoursAgo} hour${hoursAgo !== 1 ? 's' : ''} ago` : daysAgoLabel(elapsed);
    const contactLabel = attendeeContact?.name || 'the attendees';

    prompts.push({
      id:          `post-meeting-${meeting.id}`,
      type:        'post_meeting',
      description: `You met with ${contactLabel} ${timeLabel}. No follow-up has been sent.`,
      suggestion:  'Would you like to draft a follow-up?',
      primaryAction: {
        label:   'Draft follow-up email',
        type:    'mailto',
        payload: {
          to:      attendeeContact?.email || '',
          subject: `Follow-up: ${meeting.title}`,
        },
      },
      contactName:  contactLabel !== 'the attendees' ? contactLabel : null,
      contactEmail: attendeeContact?.email || null,
      target:       attendeeContact ? { view: 'Contacts' } : null,
    });
  }

  return prompts;
}

// ── Trigger 2 — Unanswered commitments ───────────────────────────────────────
export function checkCommitments(tasks) {
  const now  = Date.now();
  const prompts = [];

  for (const task of tasks) {
    if (task.completed) continue;
    if (!titleContains(task.title, COMMITMENT_WORDS)) continue;

    const due = new Date(task.deadline || task.datetime).getTime();
    if (isNaN(due) || due > now) continue; // only due/overdue

    const elapsed   = now - due;
    const dateLabel = due === 0 ? 'today' : daysUntilLabel(-elapsed) === 'today' ? 'today' : daysAgoLabel(elapsed);

    prompts.push({
      id:          `commitment-${task.id}`,
      type:        'commitment',
      description: `You have an outstanding commitment: "${task.title}"`,
      suggestion:  `This was due ${dateLabel}. Do you want to action this now?`,
      primaryAction: {
        label:   'Open task',
        type:    'open_task',
        payload: { taskId: task.id },
      },
      taskId: task.id,
      target: { view: 'Tasks', taskId: task.id },
    });
  }

  return prompts;
}

// ── Trigger 3 — Email no reply ────────────────────────────────────────────────
export function checkEmailNoReply(minDays = 5, contacts = []) {
  const now      = Date.now();
  const cutoff   = minDays * MS_DAY;
  const prompts  = [];

  for (const contact of contacts) {
    const emails = (contact.interactions || [])
      .filter(i => i.type === 'email')
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    if (!emails.length) continue;

    const mostRecent   = new Date(emails[0].date).getTime();
    const elapsed      = now - mostRecent;

    // Only trigger if last email was minDays+ ago
    if (elapsed < cutoff) continue;

    // Check if there are multiple emails — if the last one is already a follow-up, skip
    if (emails[0].summary?.toLowerCase().includes('follow-up') ||
        emails[0].summary?.toLowerCase().includes('following up')) continue;

    prompts.push({
      id:          `email-noreply-${contact.id}`,
      type:        'email_no_reply',
      description: `${contact.name} hasn't replied to your email from ${daysAgoLabel(elapsed)}.`,
      suggestion:  'Would you like to send a follow-up?',
      primaryAction: {
        label:   'Draft follow-up email',
        type:    'mailto',
        payload: {
          to:      contact.email || '',
          subject: `Following up`,
        },
      },
      contactName:  contact.name,
      contactEmail: contact.email,
      target:       { view: 'Contacts' },
    });
  }

  return prompts;
}

// ── Trigger 4 — Proposal / brief not sent ─────────────────────────────────────
export function checkProposals(tasks, contacts = []) {
  const now      = Date.now();
  const prompts  = [];

  for (const task of tasks) {
    if (task.completed) continue;
    if (!titleContains(task.title, PROPOSAL_WORDS)) continue;

    const due     = new Date(task.deadline || task.datetime).getTime();
    if (isNaN(due)) continue;

    const msToDue = due - now;
    const isOverdue    = msToDue < 0;
    const isDueSoon    = msToDue >= 0 && msToDue <= 24 * MS_HOUR;

    if (!isOverdue && !isDueSoon) continue;

    // Try to find a linked contact
    const words         = task.title.toLowerCase().split(/\s+/);
    const linkedContact = contacts.find(c =>
      c.name && words.some(w => w.length > 3 && c.name.toLowerCase().includes(w))
    );

    const docType = PROPOSAL_WORDS.find(w => task.title.toLowerCase().includes(w)) || 'document';
    const dueLabel = isOverdue ? daysAgoLabel(now - due) : 'today';
    const contactLabel = linkedContact?.name || 'the client';

    prompts.push({
      id:          `proposal-${task.id}`,
      type:        'proposal',
      description: `Your ${docType} for ${contactLabel} ${isOverdue ? `was due ${dueLabel}` : 'is due today'}.`,
      suggestion:  'Has this been sent?',
      primaryAction: {
        label:   linkedContact?.email ? 'Draft follow-up email' : 'Open task',
        type:    linkedContact?.email ? 'mailto' : 'open_task',
        payload: linkedContact?.email
          ? { to: linkedContact.email, subject: `${docType.charAt(0).toUpperCase() + docType.slice(1)}: ${task.title}` }
          : { taskId: task.id },
      },
      secondaryAction: {
        label:   'Mark as sent',
        type:    'mark_sent',
        payload: { taskId: task.id },
      },
      contactName:  linkedContact?.name || null,
      contactEmail: linkedContact?.email || null,
      taskId:       task.id,
      target:       { view: 'Tasks', taskId: task.id },
    });
  }

  return prompts;
}

// ── Public: generate all prompts ──────────────────────────────────────────────
export function generateFollowUpPrompts(tasks = [], googleEvents = [], icalEvents = [], outlookEvents = [], prefs = {}, contacts = []) {
  const minDays = parseInt(prefs.followUpEmailDays, 10) || 5;
  const prompts = [
    ...checkPostMeeting(tasks, googleEvents, icalEvents, outlookEvents, contacts),
    ...checkCommitments(tasks),
    ...checkEmailNoReply(minDays, contacts),
    ...checkProposals(tasks, contacts),
  ];

  // Deduplicate by ID
  const seen = new Set();
  return prompts.filter(p => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
}
